import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { Alert, CircularProgress, Snackbar } from "@mui/material";
import { extractApiError } from "@/shared/api/apiClient";

// Uygulama genelinde uzun süren işlemler (özellikle silme) için tek bir üstte-ortada bildirim.
// İmperatif API olduğundan hem React Query useMutation akışlarına hem de düz async handler'lara uyar.

type NoticePhase = "deleting" | "done" | "error";

interface Notice {
  phase: NoticePhase;
  label: string;
}

interface DeleteHandlers {
  onMutate: () => void;
  onSuccess: () => void;
  onError: (error: unknown) => void;
}

interface ActionNoticeApi {
  /** Düşük seviye: bildirimi doğrudan göster. */
  show: (notice: Notice) => void;
  /** Bildirimi kapat. */
  clear: () => void;
  /**
   * React Query useMutation için hazır lifecycle callback'leri üretir.
   * `{label} siliniyor… → {label} silindi`; hata olursa API mesajını gösterir.
   */
  forDelete: (
    label: string,
    opts?: { onSuccess?: () => void; onError?: (error: unknown) => void },
  ) => DeleteHandlers;
  /**
   * Düz async handler'lar için: başta ilerleme metni, başarıda bitiş metni; hata olursa error
   * gösterir ve hatayı tekrar fırlatır (çağıran catch ile devam eder). Varsayılan metin silme
   * içindir (`{label} siliniyor… / {label} silindi`); silme dışı işlemler `opts` ile kendi
   * metnini geçer (ör. oluşturma/taşıma) — yanlışlıkla "silindi" göstermemek için.
   */
  run: <T>(
    label: string,
    fn: () => Promise<T>,
    opts?: { busyText?: string; doneText?: string },
  ) => Promise<T>;
}

const ActionNoticeCtx = createContext<ActionNoticeApi | null>(null);

export const ActionNoticeProvider = ({ children }: { children: ReactNode }) => {
  const [notice, setNotice] = useState<Notice | null>(null);

  const show = useCallback((n: Notice) => setNotice(n), []);
  const clear = useCallback(() => setNotice(null), []);

  const forDelete = useCallback<ActionNoticeApi["forDelete"]>(
    (label, opts) => ({
      onMutate: () => setNotice({ phase: "deleting", label: `${label} siliniyor…` }),
      onSuccess: () => {
        setNotice({ phase: "done", label: `${label} silindi` });
        opts?.onSuccess?.();
      },
      onError: (error: unknown) => {
        setNotice({ phase: "error", label: extractApiError(error) });
        opts?.onError?.(error);
      },
    }),
    [],
  );

  const run = useCallback<ActionNoticeApi["run"]>(async (label, fn, opts) => {
    // "deleting" fazı yalnız "meşgul" durumu (spinner + auto-hide kapalı) demektir; metin opsiyoneldir.
    setNotice({ phase: "deleting", label: opts?.busyText ?? `${label} siliniyor…` });
    try {
      const result = await fn();
      setNotice({ phase: "done", label: opts?.doneText ?? `${label} silindi` });
      return result;
    } catch (error) {
      setNotice({ phase: "error", label: extractApiError(error) });
      throw error;
    }
  }, []);

  const api = useMemo<ActionNoticeApi>(
    () => ({ show, clear, forDelete, run }),
    [show, clear, forDelete, run],
  );

  const isDeleting = notice?.phase === "deleting";

  return (
    <ActionNoticeCtx.Provider value={api}>
      {children}
      <Snackbar
        open={!!notice}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        autoHideDuration={notice && !isDeleting ? 2500 : null}
        onClose={(_, reason) => {
          if (reason !== "clickaway") setNotice(null);
        }}
        sx={{ mt: { xs: 7, sm: 8 } }}
      >
        <Alert
          variant="filled"
          severity={notice?.phase === "error" ? "error" : notice?.phase === "done" ? "success" : "info"}
          icon={isDeleting ? <CircularProgress size={16} color="inherit" /> : undefined}
          onClose={isDeleting ? undefined : () => setNotice(null)}
          sx={{ boxShadow: 3, alignItems: "center" }}
        >
          {notice?.label ?? ""}
        </Alert>
      </Snackbar>
    </ActionNoticeCtx.Provider>
  );
};

export const useActionNotice = (): ActionNoticeApi => {
  const ctx = useContext(ActionNoticeCtx);
  if (!ctx) throw new Error("useActionNotice must be used inside ActionNoticeProvider");
  return ctx;
};
